import React, { useEffect, useRef, useState } from "react";

import { Alert, Button, Chip, Icon, StatePanel, Textarea, useToast } from "../../components/ui";
import { waBroadcastApi, apiError } from "../../waBroadcast/waBroadcastApi";
import { WhatsAppText } from "../../waBroadcast/WhatsAppText";

const TYPES = [
  { id: "listing", label: "Listings" },
  { id: "order", label: "Order requests" },
  { id: "sell", label: "Sell requests" },
];

/** The template with every {variable} filled by its example, for the live preview. */
const preview = (template, variables) => {
  const byKey = Object.fromEntries((variables || []).map((v) => [v.key, v.example]));
  return template.replace(/\{([a-z0-9_]+)\}/gi, (m, key) => byKey[key.toLowerCase()] ?? m);
};

/**
 * Owner Admin → WhatsApp Broadcast → Message templates (Alex, 2026-10-16).
 * Store owners and store admins write their own "Share to Broadcast" message
 * for listings, order requests and sell requests, placing {variables} where
 * the item's details go. Until a store saves its own, the built-in format is
 * used. Saves when the box is left (no Save button); "Use the built-in format"
 * switches back.
 */
export function WaTemplatesPanel() {
  const toast = useToast();
  const [data, setData] = useState(null);
  const [type, setType] = useState("listing");
  const [drafts, setDrafts] = useState({});
  const [save, setSave] = useState("idle");
  const [error, setError] = useState("");
  const boxRef = useRef(null);

  const apply = (res) => {
    setData(res);
    setDrafts(Object.fromEntries(TYPES.map(({ id }) => [id, res.templates[id].template])));
  };

  useEffect(() => {
    let alive = true;
    waBroadcastApi.templates().then((res) => alive && apply(res)).catch((err) => alive && setError(apiError(err, "Could not load the message templates.")));
    return () => { alive = false; };
  }, []);

  if (!data) {
    return error ? <Alert tone="error" title={error} /> : <StatePanel state="loading" loadingShape="row" compact />;
  }

  const current = data.templates[type];
  const draft = drafts[type] ?? "";
  const label = TYPES.find((t) => t.id === type).label.toLowerCase();

  const persist = async (value, message) => {
    setSave("saving");
    setError("");
    try {
      apply(await waBroadcastApi.setTemplates({ [type]: value }));
      setSave("saved");
      toast.success(message, { description: "New “Share to Broadcast” messages for " + label + " use it from now on." });
    } catch (err) {
      setSave("idle");
      setError(apiError(err, "Couldn't save the template — try again."));
    }
  };

  const saveDraft = () => {
    if (draft === current.template) return;
    if (!draft.trim()) {
      setError("A template can't be empty — use “Use the built-in format” to go back to ours.");
      return;
    }
    persist(draft, `Template for ${label} saved`);
  };

  // Put {key} where the cursor is (or at the end), then save it.
  const insert = (key) => {
    const el = boxRef.current;
    const token = `{${key}}`;
    const at = el && typeof el.selectionStart === "number" ? el.selectionStart : draft.length;
    const end = el && typeof el.selectionEnd === "number" ? el.selectionEnd : at;
    const next = draft.slice(0, at) + token + draft.slice(end);
    setDrafts({ ...drafts, [type]: next });
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange?.(at + token.length, at + token.length);
    });
  };

  return (
    <div className="wa-numbers-panel__block wa-templates">
      <h4 className="wa-numbers-panel__title rc-wab-heading">Message templates</h4>
      <p className="wa-numbers-panel__note">
        Write your own message for each kind of item. Tap a detail to place it where the cursor is — it is filled in for each item when you share. Leave
        a detail empty and it reads “N/A”. Closing lines are still added at the end (never for order requests).
      </p>

      <div className="wab-chips" role="tablist" aria-label="Kind of message">
        {TYPES.map((t) => (
          <Chip key={t.id} role="tab" aria-selected={type === t.id} selected={type === t.id} onClick={() => setType(t.id)}>
            {t.label}{data.templates[t.id].custom ? " · yours" : ""}
          </Chip>
        ))}
      </div>

      <Textarea
        ref={boxRef}
        value={draft}
        onChange={(e) => setDrafts({ ...drafts, [type]: e.target.value })}
        onBlur={saveDraft}
        minRows={8}
        maxRows={18}
        maxLength={data.max_length}
        fullWidth
        aria-label={`Template for ${label}`}
      />

      <div className="wa-templates__vars" aria-label="Details you can place">
        {current.variables.map((v) => (
          <button
            key={v.key}
            type="button"
            className="wa-templates__var"
            // Keep the cursor in the box while a detail is tapped.
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => insert(v.key)}
            title={`Example: ${v.example}`}
          >
            {v.label} <code>{`{${v.key}}`}</code>
          </button>
        ))}
      </div>

      <div className="wa-templates__preview">
        <span className="wa-numbers-panel__note">How it looks in WhatsApp (example values)</span>
        <WhatsAppText className="wab-bubble__render" text={preview(draft, current.variables)} />
      </div>

      <div className="wa-templates__actions">
        <Button
          variant="secondary"
          size="sm"
          leadingIcon={<Icon name="Undo2" size="sm" />}
          disabled={!current.custom && draft === current.default}
          onClick={() => persist(null, `${TYPES.find((t) => t.id === type).label} use the built-in format again`)}
        >
          Use the built-in format
        </Button>
        <span className="wa-numbers-panel__note" aria-live="polite">
          {save === "saving" ? "Saving…" : current.custom ? "Your own template is in use" : "Using the built-in format"}
        </span>
      </div>
      {error && <Alert tone="error" title={error} />}
    </div>
  );
}

export default WaTemplatesPanel;
